// DentalBridgesData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string;
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const dentalBridges: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    en: {
        "url": "",
        "meta": {
            "title": "Dental Bridges Dubai",
            "description": "Elite Body Home to help you recover your smile with full confidence. We train our experienced team as per the quality required, to bridge the gap in between.",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "Dental Bridges Dubai"],
        "hero": {
            "title": "Dental Bridges Dubai",
            "subtitle": "Recover Your Smile With Full Confidence",
            "description": "Elite Body Home to help you recover your smile with full confidence. We train our experienced team as per the quality required, to bridge the gap in between. So whether your goal is to boost your smile aesthetics, improve functionality, or prevent dental shifting, our dental bridge treatments provide a permanent, natural-looking solution. We have state of the art clinic located in Dubai where, with our high-tech instruments, we can help you achieve a beautiful and healthy smile that lasts, our personalized approach ensures that you receive the highest standard of dental care."
        },
        "howItWorks": {
            "title": "Dental Bridge Treatment Procedure in Steps",
            "description": "The process of getting a dental bridge is simple, usually requiring two to three visits to your dentist. Dental bridges work by securing the false tooth (or teeth) (called pontics) to adjacent natural teeth or to dental implants."
        },
        "procedureSteps": {
            "heading": "Here's a rundown of the process:",
            "para": "",
            "steps": [
                {
                    "title": "Initial Consultation",
                    "desc": "Your dentist will examine your oral health and determine whether you are a good candidate for a bridge. You might also get X-rays to check the area around the tooth and jawbone.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Preparation of Adjacent Teeth",
                    "desc": "Abutment teeth are prepared by reshaping the adjacent teeth to make room for crowns that are used to support the bridge.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Impression of Your Teeth",
                    "desc": "A mold of your teeth will be used to create a customized fit for the bridge. Based on your needs, the dentist will choose the right material for your dental crown, which may be porcelain, ceramic, or metal.",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "Temporary Bridge Placement",
                    "desc": "A temporary bridge: A temporary bridge is placed over the prepared teeth while the permanent bridge is being fabricated in a dental lab.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "Fitting the Permanent Bridge",
                    "desc": "When the permanent bridge is finished it is tested for fit, adjusted if it so happens, and then firmly cemented in place.",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "Final Adjustments & Follow-Up",
                    "desc": "The dentist or oral surgery will confirm that the bridge is correctly positioned, making any necessary adjustments for comfort and function. A follow-up visit is usually booked to check progress.",
                    "icon": "fa-solid fa-stethoscope"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "There are many benefits of All-on-4 dental bridges besides the pure aesthetic that they provide that goes far deeper than your smile.",
            "para": "Dental implants are the right technology for altering life for the good.",
            "items": [
                {
                    "title": "Restores Chewing Function",
                    "desc": "If you are missing teeth, it can be hard and unpleasant to eat. With a dental bridge, you can chew fully as before and enjoy your favorite foods free of pain.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Prevents Teeth from Shifting",
                    "desc": "The gaps created from lost teeth can allow surrounding teeth to drift out of position, resulting in bite issues or jaw pain. Dental bridges help to keep your teeth in alignment.",
                    "icon": "fa-solid fa-arrows-left-right"
                },
                {
                    "title": "Improves Speech",
                    "desc": "Losing teeth — particularly in the front of the mouth — can impair speech. Bridges fill in those gaps and help you with the right pronunciation of words.",
                    "icon": "fa-solid fa-comment-dots"
                },
                {
                    "title": "Boosts Self-Confidence",
                    "desc": "Missing teeth may affect your smile and self-esteem. Providing a natural smile and confidence to interact without worry, dental bridges replace missing teeth.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "For many people who have lost one, two, or several teeth, dental bridges can be a terrific solution.",
            "para": "But not everyone is a candidate for that treatment. Here's who is eligible:",
            "items": [
                {
                    "title": "Good Oral Health",
                    "desc": "Your remaining teeth and gums must be healthy and disease free to support a bridge.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Adequate Bone Structure",
                    "desc": "The technical aspects must be strong enough to support the bridge and sufficient jawbone needs to be present to anchor the teeth properly.",
                    "icon": "fa-solid fa-bone"
                },
                {
                    "title": "Missing Teeth — One or Multiple",
                    "desc": "Dental bridges are typically the right choice for people who have lost one or more adjacent teeth — not an entire arch of teeth.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Non-Smokers",
                    "desc": "Smoking may impact the healing and long-term success of a dental bridge.",
                    "icon": "fa-solid fa-smoking-ban"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "Some preparation is needed before the dental bridge process begins.",
            "para": "By following these steps, you can ensure that your procedure goes smoothly and your dental bridge lasts longer.",
            "items": [
                {
                    "title": "Maintain Good Oral Hygiene",
                    "desc": "Regularly brush and floss to keep your gums and adjacent teeth healthy.",
                    "icon": "fa-solid fa-brush"
                },
                {
                    "title": "Address Pre-Existing Conditions",
                    "desc": "In most cases, any dental problems, such as gum disease or tooth decay, will have to be addressed before the bridge can be placed.",
                    "icon": "fa-solid fa-clipboard-check"
                },
                {
                    "title": "Discuss Material Options",
                    "desc": "Depending on your preference and budget, talk to your dentist about what is ideal for your dental bridge, and whether porcelain, ceramic, or metal materials are the best match.",
                    "icon": "fa-solid fa-comments"
                },
                {
                    "title": "Take Pain Medication (if prescribed)",
                    "desc": "You might need over-the-counter pain relief or medication prescribed by your dentist in the days after the procedure to help with any discomfort.",
                    "icon": "fa-solid fa-pills"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "Ultimately, the effects of receiving a dental bridge are both immediate & long-lasting.",
            "para": "Post-treatment, patients typically demonstrate both enhanced functionality and improved aesthetics, in addition to better oral health.",
            "items": [
                {
                    "title": "Regained Chewing & Speaking Function",
                    "desc": "Replacement teeth are securely placed in the mouth, and patients can chew food and speak without concerns over clarity, immediately after the bridge is affiliated.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Enhanced Aesthetic Appearance",
                    "desc": "It restores the aesthetics of your smile by filling in the gaps and can also enhance overall facial structure, helping you feel more confident in your appearance.",
                    "icon": "fa-solid fa-face-smile-beam"
                },
                {
                    "title": "Prevention of Teeth Shifting",
                    "desc": "Bridges prevent adjacent teeth from moving into the empty space, preserving proper tooth alignment and helping to avoid future dental issues.",
                    "icon": "fa-solid fa-arrows-left-right"
                },
                {
                    "title": "The Long-Term Oral Health Benefits",
                    "desc": "When taken care of, dental bridges can help prevent bone loss, alleviate stress on nearby teeth and maintain your bite's architecture, all of which prevent ongoing oral health issues in the future.",
                    "icon": "fa-solid fa-heartbeat"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": [
            ]
        },
        "faqData": {
            "heading": "Freaquently Asked Questions",
            "subheading": "",
            "items": [
                {
                    question: "How long do dental bridges last?",
                    answer:
                        "With proper care, dental bridges can last between 5 to 15 years, and sometimes even longer. Regular dental check-ups and good oral hygiene are crucial for longevity.",
                },
                {
                    question: "Are dental bridges painful?",
                    answer:
                        "The procedure is generally not painful, as local anesthesia is used. Afterward, there may be mild discomfort, but this can be managed with pain relief medication.",
                },
                {
                    question: "Can I eat normally with a dental bridge?",
                    answer:
                        "Yes, after the bridge is fitted and any discomfort has subsided, you can eat normally. It’s best to avoid hard or sticky foods initially.",
                },
                {
                    question: "Do I need to take special care of my dental bridge?",
                    answer:
                        "It’s important to maintain good oral hygiene by brushing and flossing around the bridge daily. Regular dental cleanings and exams are also essential to keep the bridge in good shape.",
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    ar: {
        "url": "",
        "meta": {
            "title": "جسور الأسنان في دبي",
            "description": "في ايليت بودي هوم، نساعدك على استعادة ابتسامتك بثقة تامة. نقوم بتدريب فريقنا الخبير وفق أعلى معايير الجودة، لنملأ الفراغات والفجوات في أسنانك.",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "جسور الأسنان في دبي"],
        "hero": {
            "title": "جسور الأسنان في دبي",
            "subtitle": "استعد ابتسامتك بثقة تامة",
            "description": "في ايليت بودي هوم، نساعدك على استعادة ابتسامتك بثقة تامة. نقوم بتدريب فريقنا الخبير وفق أعلى معايير الجودة، لنملأ الفراغات والفجوات في أسنانك. سواء كنت تسعى لتحسين مظهر ابتسامتك، أو تعزيز وظيفة المضغ، أو منع تحرك الأسنان، أو تعويض السن المفقود، فإن علاج تركيب جسور الأسنان لدينا يوفر لك حلاً دائماً بمظهر طبيعي. سنُساعدك في عيادتنا المتطورة في دبي وباستخدام أحدث الأجهزة على تحقيق ابتسامة جميلة وصحية تدوم طويلاً، مع ضمان حصولك على أعلى مستوى من الرعاية."
        },
        "howItWorks": {
            "title": "خطوات إجراء تركيب جسر الأسنان",
            "description": "يُعد تركيب جسر الأسنان إجراء بسيط نسبيًا، ويتطلب من زيارتين إلى ثلاث زيارات لطبيب الأسنان عادةً. يعتمد الجسر السني على تثبيت الأسنان التعويضية على الأسنان الأساسية المجاورة أو على الزرعات السنية."
        },
        "procedureSteps": {
            "heading": "إليك خطوات الإجراء:",
            "para": "",
            "steps": [
                {
                    "title": "الاستشارة والتقييم الأولي",
                    "desc": "يبدأ العلاج بجلسة استشارية، حيث يقوم طبيب الأسنان بفحص صحة الفم وتحديد ما إذا كنت مرشحًا مناسبًا لتركيب جسر الأسنان. قد تُؤخذ صور أشعة سينية لفحص المنطقة المحيطة بالسن والعظم الفكي.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "تحضير الأسنان المجاورة",
                    "desc": "يقوم الطبيب بإعادة تشكيل الأسنان المجاورة لتوفير المساحة اللازمة لتثبيت التيجان التي ستحمل الجسر.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "أخذ طبعة الأسنان",
                    "desc": "تؤخذ طبعة دقيقة لفمك باستخدام قالب خاص، وذلك لتصميم الجسر السني بحيث يتناسب تمامًا مع شكل الفم والأسنان، ويُختار نوع المادة المناسبة لصناعة تاج الأسنان بناءً على حالتك، مثل البورسلين أو السيراميك أو المعدن.",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "تركيب الجسر المؤقت",
                    "desc": "يوضع جسر مؤقت فوق الأسنان المحضرة لحماية اللثة والأسنان أثناء فترة تصنيع الجسر الدائم في المختبر.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "تركيب الجسر الدائم",
                    "desc": "بعد الانتهاء من تصنيع الجسر، يتم اختباره للتأكد من ملاءمته للفم، ثم يُثبت بشكل دائم.",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "التعديل النهائي والمتابعة",
                    "desc": "يقوم طبيب الأسنان بإجراء التعديلات اللازمة لضمان الراحة والكفاءة الوظيفية، وتُحدد زيارة لاحقة لمتابعة التطورات.",
                    "icon": "fa-solid fa-stethoscope"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "لا تقتصر فوائد جسور الأسنان All-on-4 على الناحية الجمالية فقط، بل تتجاوز ذلك لتُحدث فرقًا أعمق في صحتك",
            "para": "حيث تُعد جسور الأسنان تقنية جديدة لتغيير حياتك للأفضل.",
            "items": [
                {
                    "title": "استعادة وظيفة المضغ",
                    "desc": "يجعل فقدان الأسنان الأكل مهمة صعبة ومؤلمة أحيانًا، لكن يعيد لك الجسر السني القدرة على المضغ بكفاءة والاستمتاع بأطعمتك المفضلة دون ألم.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "منع تحرك الأسنان المجاورة",
                    "desc": "قد يؤدي وجود فراغات نتيجة فقدان الأسنان إلى تحرك الأسنان المجاورة من مكانها، ما يتسبب في مشكلات في الإطباق وآلام في الفك. يحافظ تركيب جسر الأسنان على اصطفاف الأسنان الطبيعي.",
                    "icon": "fa-solid fa-arrows-left-right"
                },
                {
                    "title": "تحسين النطق",
                    "desc": "يؤثر فقدان الأسنان الأمامية سلبًا على وضوح الكلام، يتمكن الجسر من ملء هذه الفراغات ويساعدك على نطق الكلمات بشكل سليم.",
                    "icon": "fa-solid fa-comment-dots"
                },
                {
                    "title": "تعزيز الثقة بالنفس",
                    "desc": "قد تؤثر الابتسامة غير المكتملة على تقديرك لذاتك، لكن يمكن للجسر السني أن يعيد لك ابتسامتك الطبيعية ويمنحك الثقة للتواصل دون قلق.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "تعتبر جسور الأسنان خيارًا ممتازًا لكثير من الأشخاص الذين فقدوا سنًا أو أكثر، لكنها ليست مناسبة للجميع.",
            "para": "إليك من يمكنه الاستفادة من هذا العلاج:",
            "items": [
                {
                    "title": "إذا كنت تتمتع بصحة فم جيدة",
                    "desc": "يجب أن تكون الأسنان واللثة المتبقية سليمة وخالية من الالتهابات أو التسوس لتتمكن من دعم الجسر بشكل آمن.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "إذا كنت تمتلك بنية عظمية كافية",
                    "desc": "يجب أن يتوفر عظم فك قوي وكافٍ لتثبيت الأسنان الداعمة للجسر بشكل سليم ومستقر.",
                    "icon": "fa-solid fa-bone"
                },
                {
                    "title": "إذا كنت تعاني من فقدان سن أو أكثر",
                    "desc": "يُعد جسر الأسنان مناسبًا في حالات فقدان سن واحد أو عدة أسنان متجاورة، وليس لحالات فقدان صف كامل من الأسنان.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "الأشخاص غير المدخنين",
                    "desc": "قد يؤثر التدخين سلبًا على الالتئام، مما يُقلل من فرص نجاح الجسر على المدى الطويل.",
                    "icon": "fa-solid fa-smoking-ban"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "توجد بعض الخطوات الأساسية التي ينبغي اتباعها قبل البدء في إجراء جسر الأسنان",
            "para": "والتي تساهم في ضمان نجاح العلاج وزيادة عمر الجسر السني.",
            "items": [
                {
                    "title": "المحافظة على نظافة الفم والأسنان",
                    "desc": "احرص على تنظيف الأسنان بالفرشاة والخيط بانتظام للحفاظ على صحة اللثة والأسنان المجاورة.",
                    "icon": "fa-solid fa-brush"
                },
                {
                    "title": "معالجة المشكلات السنية المسبقة",
                    "desc": "يجب علاج أي حالات مرضية موجودة مسبقًا في معظم الحالات، مثل التهابات اللثة أو تسوس الأسنان، قبل تركيب الجسر.",
                    "icon": "fa-solid fa-clipboard-check"
                },
                {
                    "title": "مناقشة خيارات المواد المتاحة",
                    "desc": "ناقش مع طبيب الأسنان النوع الأنسب لجسر الأسنان سواء كان من البورسلين أو السيراميك أو المعدن، بناءً على تفضيلاتك والتكلفة.",
                    "icon": "fa-solid fa-comments"
                },
                {
                    "title": "تناول مسكنات الألم (في حال تم وصفها)",
                    "desc": "قد يوصي طبيب الأسنان بتناول مسكنات للألم، سواء المتاحة دون وصفة طبية أو الموصوفة، للتخفيف من أي ألم بعد الإجراء.",
                    "icon": "fa-solid fa-pills"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "في نهاية المطاف، تكون نتائج تركيب جسر الأسنان فورية وطويلة الأمد في آنٍ واحد.",
            "para": "فغالبًا ما يُلاحظ المرضى تحسنًا واضحًا في وظائف الفم إلى جانب المظهر الجمالي وصحة فموية محسنة بشكل عام.",
            "items": [
                {
                    "title": "استعادة وظائف المضغ والنطق",
                    "desc": "يمكن للمريض مضغ الطعام والتحدث بثقة دون القلق من عدم وضوح الكلام بعد تثبيت الجسر.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "تحسين المظهر الجمالي",
                    "desc": "يملأ الجسر الفجوات بين الأسنان، مما يعيد للابتسامة جمالها الطبيعي ويساهم في دعم البنية الوجهية وزيادة الثقة بالنفس.",
                    "icon": "fa-solid fa-face-smile-beam"
                },
                {
                    "title": "منع تحرك الأسنان المجاورة",
                    "desc": "يعمل الجسر على منع الأسنان المحيطة من الانجراف نحو الفراغ الناتج عن السن المفقود، مما يحافظ على انتظام الأسنان وتوازن الإطباق.",
                    "icon": "fa-solid fa-arrows-left-right"
                },
                {
                    "title": "دعم صحة الفم على المدى الطويل",
                    "desc": "يمكن لجسر الأسنان أن يمنع العديد من المشكلات المستقبلية إذا تم الاهتمام به بشكل جيد من خلال منع فقدان العظم، وتخفيف الضغط على الأسنان المجاورة، والحفاظ على بنية الإطباق.",
                    "icon": "fa-solid fa-heartbeat"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "",
                    "desc": "",
                    "icon": "fa-solid fa-water"
                }
            ]
        },
        "faqData": {
            "heading": "",
            "subheading": "",
            "items": [
                {
                    "question": "",
                    "answer": ""
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    shared: {
        images: {
            main: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Banndgdrer-Image-8-scaled.jpg",
            beforeAfter: [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/sdfsdgs-3.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/sdfsdgs-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/dsfsfseff.jpg"
            ],
            introductionImage: ""
        },
        product: {
            id: 14,
            title: "Dental Bridges",
            description: "Elite Body Home to help you recover your smile with full confidence. We train our experienced team as per the quality required, to bridge the gap in between.",
            price: 699,
        }
    }
};
